const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'libs/common_web/src/defs/labReportDefs.ts';
/**
 * labReportDefs.ts
 *
 * Definitions used by the LabReportsPage and the corresponding actions,
 * reducers, selectors, etc.
 */

/**
 * The id of the lab reports instance when only one lab report can be viewed at
 * a time.
 */
export const LabReportsSingleton = 'LabReportsSingleton';

/**
 * Data source to be used when loading data
 */
export type LabCumulDataSource = 'cumul-report-v2' | 'report-map';

/**
 * Maximum number of seconds for the age of a result before the backend is
 * consulted for newer data. Note that even if a result's age is older than
 * the limit, the sagas won't poll for new data until some action triggers it.
 */
export const MAX_SECONDS_BEFORE_LOOKING_FOR_NEWER_RESULTS = 5 * 60; // 5 minutes

/**
 * Miminum number of hours of the interval to check against when the backend is
 * consulted for new data. This ensures we won't miss any data even if it
 * arrives in KISIM with a significant delay; however it also means some of the
 * data we already have will be unnecessarily refetched.
 */
export const MIN_HOURS_TO_EXPECT_NEW_RESULTS_IN = 3;

/**
 * Number of days to look into the past when loading the summaries. This value
 * defines the initial value of the state's availLimit. The latter will be
 * extended as the user manually requests earlier entries. This value may be
 * overridden by initFetchInterval in the state.
 */
export const DEFAULT_INIT_AVAIL_LIMIT_NUM_DAYS = 30;

/**
 * Number of days to extend the availLimit each time when the user presses the
 * button to look for earlier data.
 */
export const EXTEND_AVAIL_LIMIT_NUM_DAYS = 30;

/**
 * The number of days to look further into past when loading the summaries
 * incrementally after a previous call examining a single day returned without
 * any results. After this, the range will be doubled each time until MAX_~ is
 * reached.
 */
export const MIN_DAYS_TO_LOAD_SUMMARIES_EN_BLOC = 5;

/**
 * When loading the summaries incrementally, each call's range should never
 * exceed this maximum length.
 */
export const MAX_DAYS_TO_LOAD_SUMMARIES_EN_BLOC = 30;

/**
 * The number of visible columns on phone/portrait
 */
export const NUM_COLUMNS_ON_PHONE_PORTRAIT = 2;

/**
 * The number of visible columns on phone/landscape
 */
export const NUM_COLUMNS_ON_PHONE_LANDSCAPE = 5;

/**
 * The number of visible columns on tablet/portrait
 */
export const NUM_COLUMNS_ON_TABLET_PORTRAIT = 6;

/**
 * The number of visible columns on tablet/landscape
 */
export const NUM_COLUMNS_ON_TABLET_LANDSCAPE = 8;

/**
 * The maximum number of columns that are visible simultaneously in any of the
 * available views.
 */
export const MAX_NUM_COLUMNS_VISIBLE = Math.max(
    NUM_COLUMNS_ON_PHONE_PORTRAIT,
    NUM_COLUMNS_ON_PHONE_LANDSCAPE,
    NUM_COLUMNS_ON_TABLET_PORTRAIT,
    NUM_COLUMNS_ON_TABLET_LANDSCAPE
);

/**
 * Number of samples to show on charts when using phone/portrait
 */
export const NUM_SAMPLES_ON_PHONE_PORTRAIT = 6;

/**
 * Number of samples to show on charts when using phone/landscape
 */
export const NUM_SAMPLES_ON_PHONE_LANDSCAPE = 12;

/**
 * Number of samples to show on charts when using tablet/portrait
 */
export const NUM_SAMPLES_ON_TABLET_PORTRAIT = 12;

/**
 * Number of samples to show on charts when using tablet/landscape
 */
export const NUM_SAMPLES_ON_TABLET_LANDSCAPE = 18;

/**
 * Maximum length of a value in the table. Any value exceeding this length will
 * be moved to the legend.
 */
export const MAX_VALUE_LENGTH = 13;

/**
 * Units exceeding this length won't be displayed within the table, and the RowDetails won't append
 * them to each value emitted.
 */
export const MAX_UNITS_LENGTH = 10;

/**
 * Minimum number of digits behind the period when truncating a number whose
 * representation length exceeds MAX_VALUE_LENGTH.
 */
export const MIN_PRECISION_WHEN_TRUNCATING = 3;

/**
 * Values for state.ui->labReports->mode
 */

export const TABLE_MODE = 'table';
export const CHART_MODE = 'chart';

/**
 * Importance values as estimated by LabUtils.estimateImportance
 */

export const HIGH_IMPORTANCE = 'high';
export const MEDIUM_IMPORTANCE = 'medium';
export const LOW_IMPORTANCE = 'low';

/**
 * Used to specify a numerical range, e.g. the range of reference. It's not specified whether
 * the minimum and/or maximum values are part of the range or not.
 */
export interface IRange {
    min?: number;
    max?: number;
}
